export type AiAgentSessionStatus = 'open' | 'processing' | 'closed' | 'waiting';

export interface ChatInteractionQuestion {
  question: string;
  answers: string[];
  actions?: string[];
}

export interface ChatInteractionPreview {
  title: string;
  description: string;
  category: string;
  questions: ChatInteractionQuestion[];
  rawJson?: Record<string, unknown>;
}

export interface ChatMessage {
  id: string;
  sender: 'user' | 'agent' | 'system' | 'status';
  text: string;
  icon?: string;
  timestamp: Date;
  /** Status chips only: 'error' chips are styled to demand attention. */
  severity?: 'info' | 'error';
  /** Status chips only: render a retry button for this failure. */
  retryable?: boolean;
  /** Status chips only: failed attempts this request has already cost. */
  retryCount?: number;
  suggestedActions?: string[];
  interactionPreview?: ChatInteractionPreview;
}

export interface QuickSuggestion {
  label: string;
  icon: string;
  prompt: string;
}

export interface AiAgentSession {
  id: string;
  title: string;
  agentName: string;
  status: AiAgentSessionStatus;
  createdAt: Date;
  updatedAt: Date;
  messages: ChatMessage[];
  createdInteractionIds?: string[];
  selectedMessageId?: string | null;
}

export interface BackendAiAgentMessage {
  _id?: string;
  id?: string;
  sender: 'user' | 'agent' | 'system' | 'tool' | 'auditor' | 'status';
  message: string;
  icon?: string;
  severity?: 'info' | 'error';
  internal?: boolean;
  tool_calls?: unknown;
  toolCalls?: unknown;
  tool_call_id?: string;
  toolCallId?: string;
  retryable?: boolean;
  retryCount?: number;
  retry_count?: number;
  createdAt: string;
}

export interface BackendAiAgentSession {
  _id?: string;
  id?: string;
  title: string;
  agentName: string;
  status: AiAgentSessionStatus;
  clientId?: string | null;
  messages: BackendAiAgentMessage[];
  created_interaction_ids?: string[];
  createdInteractionIds?: string[];
  selected_message_id?: string | null;
  selectedMessageId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AiAgentSessionCreateRequest {
  agentName?: string;
  title?: string;
  clientId?: string | null;
  initialMessage?: string | null;
}

export interface AiAgentSessionSendMessageRequest {
  message: string;
}

export interface AiAgentSessionUpdateRequest {
  title?: string;
  status?: AiAgentSessionStatus;
}

export interface AiAgentSessionCreateInteractionRequest {
  interactionData: Record<string, unknown>;
  messageId?: string | null;
  message_id?: string | null;
}

export interface AiAgentSessionWebSocketPayload {
  sessionId?: string;
  session_id?: string;
  status?: string;
  activity?: string;
  message?: string;
  icon?: string;
  callId?: string;
  call_id?: string;
  toolName?: string;
  tool_name?: string;
  friendlyMessage?: string;
  friendly_message?: string;
}

/**
 * Parses markdown json codeblock containing interaction structure.
 */
export function extractInteractionPreview(
  raw: string
): ChatInteractionPreview | undefined {
  if (!raw) {
    return undefined;
  }

  // Look for ```json ... ``` or ``` ... ``` block containing questions
  const codeBlockMatch = raw.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
  const jsonCandidate = codeBlockMatch ? codeBlockMatch[1] : raw;

  try {
    const parsed = JSON.parse(jsonCandidate.trim());
    if (
      !parsed ||
      typeof parsed !== 'object' ||
      (!Array.isArray(parsed.questions) && !Array.isArray(parsed.flow))
    ) {
      return undefined;
    }

    const topLevelAnswers = (
      Array.isArray(parsed.answers) ? parsed.answers : []
    ) as Array<Record<string, unknown>>;
    const topLevelActions = (
      Array.isArray(parsed.actions) ? parsed.actions : []
    ) as Array<Record<string, unknown>>;

    const questions: ChatInteractionQuestion[] = Array.isArray(parsed.questions)
      ? parsed.questions.map(
          (q: Record<string, unknown>, idx: number): ChatInteractionQuestion => {
            const qId = String(q['id'] ?? q['_id'] ?? `q${idx + 1}`);

            // 1. Get answers from nested array or top-level answers list
            let answers: string[] = [];
            if (Array.isArray(q['answers'])) {
              answers = (q['answers'] as Array<unknown>).map((a) =>
                typeof a === 'string' ? a : (a as any)?.value ?? ''
              );
            } else if (topLevelAnswers.length > 0) {
              answers = topLevelAnswers
                .filter(
                  (a) =>
                    a['question_id'] === qId ||
                    a['questionId'] === qId ||
                    a['question_id'] === q['id']
                )
                .map((a) => String(a['value'] ?? ''));
            }

            // 2. Get actions from nested array or top-level actions list
            let actions: string[] = [];
            if (Array.isArray(q['actions'])) {
              actions = (q['actions'] as Array<unknown>).map((act) => {
                if (typeof act === 'string') {
                  return act;
                }
                const a = act as any;
                if (a?.type === 'speak') {
                  return `Spraak: "${a.value ?? ''}"`;
                }
                if (a?.type === 'goto-question') {
                  return `Ga naar vraag ${a.value ?? ''}`;
                }
                if (a?.type === 'goto-interaction') {
                  return `Ga naar interactie: ${a.value ?? ''}`;
                }
                if (a?.type === 'stop') {
                  return 'Stop interactie';
                }
                return String(a?.value ?? a?.type ?? '');
              });
            } else if (topLevelActions.length > 0) {
              actions = topLevelActions
                .filter(
                  (act) =>
                    act['question_id'] === qId ||
                    act['questionId'] === qId ||
                    act['question_id'] === q['id']
                )
                .map((act) => {
                  const a = act as any;
                  if (a.type === 'speak') {
                    return `Spraak: "${a.value ?? ''}"`;
                  }
                  if (a.type === 'goto-question') {
                    return `Ga naar vraag ${a.value ?? ''}`;
                  }
                  if (a.type === 'goto-interaction') {
                    return `Ga naar interactie: ${a.value ?? ''}`;
                  }
                  if (a.type === 'stop') {
                    return 'Stop interactie';
                  }
                  return String(a.value ?? a.type ?? '');
                });
            }

            return {
              question:
                (q['value'] as string) ||
                (q['question'] as string) ||
                `Vraag ${idx + 1}`,
              answers: answers.filter(Boolean),
              actions: actions.filter(Boolean),
            };
          }
        )
      : [];

    return {
      title:
        (parsed['label'] as string) ||
        (parsed['title'] as string) ||
        'Nieuwe interactie',
      description:
        (parsed['description'] as string) || 'Gegenereerd door AI Agent',
      category: (parsed['category'] as string) || 'Interactie',
      questions,
      rawJson: parsed,
    };
  } catch {
    return undefined;
  }
}

/**
 * Strips raw JSON codeblock from message text if a preview card is rendered.
 */
export function cleanMessageText(
  text: string,
  hasPreview: boolean
): string {
  if (!hasPreview || !text) {
    return text || '';
  }

  // Remove the ```json ... ``` codeblock and raw json object cleanly, preserving
  // paragraph breaks. The object pattern stays greedy on purpose: `questions`
  // holds objects of its own, so a lazy match would stop at the first nested
  // `}` and leave the tail of the proposal on screen.
  const cleaned = text
    .replace(/```(?:json)?\s*[\s\S]*?```/gi, '\n\n')
    .replace(/\{[\s\S]*"questions"[\s\S]*\}/g, '\n\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  return cleaned;
}

/**
 * Maps a backend response session to the UI model.
 */
export function mapBackendSessionToUi(
  s: BackendAiAgentSession
): AiAgentSession {
  const sessionId = s._id || s.id || `session-${Date.now()}`;
  const messages: ChatMessage[] = (s.messages || [])
    .filter(
      (m) =>
        !m.internal &&
        m.sender !== 'tool' &&
        m.sender !== 'auditor' &&
        m.sender !== 'system' &&
        !m.tool_calls &&
        !m.toolCalls
    )
    .map((m): ChatMessage => {
      const rawText = m.message || '';
      const preview =
        m.sender === 'agent' ? extractInteractionPreview(rawText) : undefined;
      const displayText = cleanMessageText(rawText, preview !== undefined);
      const senderRole: 'user' | 'agent' | 'system' | 'status' =
        m.sender === 'status'
          ? 'status'
          : m.sender === 'user'
          ? 'user'
          : 'agent';

      return {
        id: m._id || m.id || `msg-${Date.now()}-${Math.random()}`,
        sender: senderRole,
        text: displayText,
        icon: m.icon,
        timestamp: m.createdAt ? new Date(m.createdAt) : new Date(),
        severity: m.severity ?? 'info',
        retryable: m.retryable ?? false,
        retryCount: m.retryCount ?? m.retry_count ?? 0,
        interactionPreview: preview,
      };
    })
    .filter(
      (m) => m.text.trim().length > 0 || m.interactionPreview !== undefined
    );

  // Only the newest visible message may offer a retry. A failure that was
  // already retried successfully keeps `retryable` on record, but its button
  // is stale -- and the backend refuses it -- so it must not stay clickable.
  messages.forEach((m, i) => {
    if (m.retryable && i !== messages.length - 1) {
      m.retryable = false;
    }
  });

  return {
    id: sessionId,
    title: s.title || 'Nieuwe interactie',
    agentName: s.agentName || 'interaction_creator',
    status: s.status || 'open',
    createdAt: s.createdAt ? new Date(s.createdAt) : new Date(),
    updatedAt: s.updatedAt ? new Date(s.updatedAt) : new Date(),
    messages,
    createdInteractionIds: s.created_interaction_ids || s.createdInteractionIds || [],
    selectedMessageId: s.selected_message_id || s.selectedMessageId || null,
  };
}
